import { useLocalSearchParams } from 'expo-router';
import React, { useMemo } from 'react';
import { type AccessibilityRole, Platform, View } from 'react-native';

import { Box } from '@fhs-legacy/universal-components';
import { IBackendCart } from '@rbi-ctg/menu';
import { IHistoricalOrder } from '@rbi-ctg/menu';
import { BlankScreen } from 'components/blank-screen';
import LoadingAnimation from 'components/loading-animation';
import { ILoyaltyTransactionV2, ServiceMode } from 'generated/graphql-gateway';
import { useAppSelector } from 'state/global-state';
import { selectLoyaltyUserTransactions } from 'state/global-state/models/loyalty/user/user.selectors';
import { useUIContext } from 'state/ui';

import { CartEntry } from './cart-entry';
import {
  Container,
  Divider, 
  LoadingContainer, 
  OrderDetailsScrollContainer,
} from './details-modal.styled';
import { ReceiptDetails } from './receipt-details';
import { RECEIPT_ORDER_DETAILS_TITLE_NATIVE_ID, ReceiptHeadInfo } from './receipt-head-info';
import {
  calculateSubTotalCents,
  calculateTotalCents,
  parseLoyaltyTransactionToCartEntry,
} from './utils';

export function RestaurantReceipt() {
  const params = useLocalSearchParams<{ rbiOrderId: string }>();
  const loyaltyTransactionId = params.rbiOrderId?.toString() ?? '';
  const { formatCurrencyForLocale } = useUIContext();

  const loyaltyTransactions = useAppSelector(selectLoyaltyUserTransactions);
  const isLoyaltyTransactionsLoading = !loyaltyTransactions.length;

  const loyaltyTransaction = loyaltyTransactions.find(t => t.id === loyaltyTransactionId);
  const loyaltyTransactionAsOrder = useMemo(() => {
    if (isLoyaltyTransactionsLoading || !loyaltyTransaction) {
      return null;
    }
    const cartEntries = parseLoyaltyTransactionToCartEntry(
      loyaltyTransaction as ILoyaltyTransactionV2
    );
    return {
      createdAt: loyaltyTransaction.createdAt,
      cart: {
        cartEntries,
        serviceMode: ServiceMode.EAT_IN,
        totalCents: calculateTotalCents(loyaltyTransaction),
        subTotalCents: calculateSubTotalCents(loyaltyTransaction),
      } as unknown as IBackendCart,
      loyaltyTransaction,
    } as unknown as IHistoricalOrder;
  }, [loyaltyTransaction, isLoyaltyTransactionsLoading]);

  return (
    <View style={{ flex: 1 }}>
      <Container style={{ flex: 1 }}>
        {loyaltyTransactionAsOrder ? (
          <OrderDetailsScrollContainer
            {...(Platform.OS === 'web'
              ? {
                  accessibilityRole: 'main' as AccessibilityRole,
                  accessibilityLabelledBy: RECEIPT_ORDER_DETAILS_TITLE_NATIVE_ID,
                }
              : {})}
          >
            <View>
              <ReceiptHeadInfo order={loyaltyTransactionAsOrder} />
              <Divider />
            </View>
            <Box accessibilityRole={'list' as AccessibilityRole}>
              {loyaltyTransactionAsOrder.cart.cartEntries.map((entry, i) => {
                if (entry.lineId === 'discount-offer') {
                  return null;
                }
                return (
                  <Box key={entry.name! + i} accessibilityRole={'listitem' as AccessibilityRole}>
                    <CartEntry
                      currencyFormatter={formatCurrencyForLocale}
                      entry={entry}
                      index={i}
                    />
                    <Divider accessibilityRole="none" importantForAccessibility="no" />
                  </Box>
                );
              })}
            </Box>
            <View>
              <ReceiptDetails order={loyaltyTransactionAsOrder} />
            </View>
            {/* Creates padding from the bottom so the text doesnt get cut off */}
            <View style={{ height: 16 }} />
          </OrderDetailsScrollContainer>
        ) : isLoyaltyTransactionsLoading ? (
          <LoadingContainer>
            <LoadingAnimation />
          </LoadingContainer>
        ) : (
          <BlankScreen context={{ reason: 'loyalty transaction not found' }} />
        )}
      </Container>
    </View>
  );
}

export default RestaurantReceipt;
